"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { readChainWorkBeat } from "@/features/finance/chain-settlement";
import { useOffers } from "@/features/finance/hooks/useOffers";
import { canAcceptOffer, canCancelOffer } from "@/features/finance/offer-eligibility";
import type { OfferItem } from "@/features/finance/types";
import { subscribeToRealtimeMessages } from "@/features/realtime/bus";
import { useWallets } from "@/features/wallet/hooks/useWallets";
import { useTransactionLedger } from "@/lib/transaction-flow";
import { useAuthStore } from "@/store/useAuthStore";
import type { ActiveFlow, NftFinanceProps } from "./financeTypes";
const OFFERS_PAGE_SIZE = 10;

/**
 * THE NFT PAGE'S COMMERCE STATE — the legacy FinanceBody's logic, moved
 * verbatim so a design only draws it: the open flow, the session ledger
 * (`nftbaz:txn:{contract}:{token}`, kept across `router.refresh()`), the
 * offers (`useOffers`, ten a page, sorted by total), the page's own ear on the
 * chain (any SETTLED beat refetches the offers and refreshes the page), the
 * login gate on every action, the viewer's wallets for recognising their own
 * offers, and which single primary action this viewer gets:
 * owner + listed → cancel the listing; owner + not listed → list;
 * visitor + active NFT → place an offer; visitor + inactive → nothing.
 */
export function useNftFinance(props) {
  const $ = _c(81);
  const {
    lang,
    contractAddress,
    tokenId,
    networkName,
    currencySymbol,
    listingCurrencySymbol: t0,
    currencyId,
    networkId,
    isListed,
    listingPrice,
    isOwner,
    isNftActive: t1,
    nftName,
    nftImage,
    collectionName,
    royaltyPercent: t2,
    listingQuantity: t3,
    listingCurrencyAddress: t4
  } = props;
  const listingCurrencySymbol = t0 === undefined ? null : t0;
  const isNftActive = t1 === undefined ? true : t1;
  const royaltyPercent = t2 === undefined ? null : t2;
  const listingQuantity = t3 === undefined ? null : t3;
  const listingCurrencyAddress = t4 === undefined ? null : t4;
  const {
    user,
    openLoginModal
  } = useAuthStore();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [flow, setFlow] = useState(null);
  const [offersPage, setOffersPage] = useState(1);
  const {
    records,
    record,
    dismiss
  } = useTransactionLedger(`nftbaz:txn:${contractAddress}:${tokenId}`);
  let t5;
  if ($[0] !== offersPage) {
    t5 = {
      limit: OFFERS_PAGE_SIZE,
      page: offersPage
    };
    $[0] = offersPage;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const {
    data: offersData,
    isLoading: isOffersLoading,
    isError: isOffersError,
    isFetching: isOffersFetching,
    refetch: refetchOffers
  } = useOffers(contractAddress, tokenId, t5);
  let t6;
  if ($[2] !== offersData?.data?.items) {
    t6 = offersData?.data?.items ?? [];
    $[2] = offersData?.data?.items;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const offers = t6;
  let t7;
  if ($[4] !== offers) {
    t7 = [...offers].sort(_temp);
    $[4] = offers;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const sortedOffers = t7;
  const offersPageMeta = offersData?.data?.page;
  const offersTotalCount = offersPageMeta?.total ?? sortedOffers.length;
  let t8;
  if ($[6] !== contractAddress || $[7] !== queryClient || $[8] !== refetchOffers || $[9] !== tokenId) {
    t8 = async () => {
      await queryClient.invalidateQueries({
        queryKey: ["nft-offers", contractAddress, tokenId]
      });
      await refetchOffers();
    };
    $[6] = contractAddress;
    $[7] = queryClient;
    $[8] = refetchOffers;
    $[9] = tokenId;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const refreshOffers = t8;
  const onSettlementRef = useRef(_temp2);
  let t9;
  if ($[11] !== refreshOffers || $[12] !== router) {
    t9 = () => {
      onSettlementRef.current = () => {
        refreshOffers();
        router.refresh();
      };
    };
    $[11] = refreshOffers;
    $[12] = router;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  useEffect(t9);
  let t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => subscribeToRealtimeMessages(message => {
      if (readChainWorkBeat(message?.key)?.beat !== "settled") {
        return;
      }
      onSettlementRef.current();
    });
    t11 = [];
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  const assetName = nftName || `#${tokenId}`;
  let t12;
  if ($[16] !== currencySymbol || $[17] !== networkName) {
    t12 = networkName ? networkName.toUpperCase() : currencySymbol;
    $[16] = currencySymbol;
    $[17] = networkName;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const networkLabel = t12;
  let t13;
  if ($[19] !== openLoginModal || $[20] !== user) {
    t13 = open => {
      if (!user) {
        openLoginModal();
        return;
      }
      open();
    };
    $[19] = openLoginModal;
    $[20] = user;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const gate = t13;
  const viewerId = user?.id ? Number(user.id) : null;
  const {
    data: walletsData
  } = useWallets();
  let t14;
  if ($[22] !== walletsData?.data?.wallets) {
    t14 = (walletsData?.data?.wallets ?? []).map(_temp3).filter(_temp4);
    $[22] = walletsData?.data?.wallets;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const viewerAddresses = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setFlow(null);
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const closeFlow = t15;
  let t16;
  if ($[25] !== assetName || $[26] !== collectionName || $[27] !== contractAddress || $[28] !== lang || $[29] !== networkLabel || $[30] !== nftImage || $[31] !== record || $[32] !== royaltyPercent || $[33] !== tokenId) {
    t16 = {
      lang,
      assetName,
      assetImage: nftImage,
      collectionName,
      tokenId,
      networkLabel,
      royaltyPercent,
      contractAddress,
      onRecord: record
    };
    $[25] = assetName;
    $[26] = collectionName;
    $[27] = contractAddress;
    $[28] = lang;
    $[29] = networkLabel;
    $[30] = nftImage;
    $[31] = record;
    $[32] = royaltyPercent;
    $[33] = tokenId;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const shared = t16;
  const primary = isOwner ? isListed ? "cancel-listing" : "list" : isNftActive ? "offer" : null;
  let t17;
  if ($[35] !== gate || $[36] !== primary) {
    t17 = () => {
      if (primary === "cancel-listing") {
        gate(() => setFlow({
          kind: "cancel-listing"
        }));
      } else {
        if (primary === "list") {
          gate(() => setFlow({
            kind: "list"
          }));
        } else {
          if (primary === "offer") {
            gate(() => setFlow({
              kind: "offer"
            }));
          }
        }
      }
    };
    $[35] = gate;
    $[36] = primary;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const openPrimary = t17;
  let t18;
  if ($[38] !== gate) {
    t18 = () => gate(() => setFlow({
      kind: "offer"
    }));
    $[38] = gate;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  const openOffer = t18;
  let t19;
  if ($[40] !== gate) {
    t19 = offer => gate(() => setFlow({
      kind: "accept-offer",
      offer
    }));
    $[40] = gate;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  const acceptOffer = t19;
  let t20;
  if ($[42] !== gate) {
    t20 = offer_0 => gate(() => setFlow({
      kind: "cancel-offer",
      offer: offer_0
    }));
    $[42] = gate;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  const cancelOffer = t20;
  let t21;
  if ($[44] !== isOwner || $[45] !== viewerAddresses || $[46] !== viewerId) {
    t21 = offer_1 => {
      const offerUserId = offer_1.user_id ? Number(offer_1.user_id) : null;
      const ours = Number(offer_1.id) > 0;
      return {
        mine: viewerId != null && offerUserId === viewerId,
        canAccept: ours && canAcceptOffer(offer_1, isOwner),
        canCancel: ours && canCancelOffer(offer_1, viewerId, viewerAddresses)
      };
    };
    $[44] = isOwner;
    $[45] = viewerAddresses;
    $[46] = viewerId;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  const offerRights = t21;
  const t22 = lang === "fa";
  let t23;
  if ($[48] !== acceptOffer || $[49] !== cancelOffer || $[50] !== currencyId || $[51] !== currencySymbol || $[52] !== dismiss || $[53] !== flow || $[54] !== isListed || $[55] !== isNftActive || $[56] !== isOffersError || $[57] !== isOffersFetching || $[58] !== isOffersLoading || $[59] !== isOwner || $[60] !== lang || $[61] !== listingCurrencyAddress || $[62] !== listingCurrencySymbol || $[63] !== listingPrice || $[64] !== listingQuantity || $[65] !== networkId || $[66] !== networkName || $[67] !== offerRights || $[68] !== offersPageMeta || $[69] !== offersTotalCount || $[70] !== openOffer || $[71] !== openPrimary || $[72] !== primary || $[73] !== records || $[74] !== refetchOffers || $[75] !== refreshOffers || $[76] !== router || $[77] !== shared || $[78] !== sortedOffers || $[79] !== t22) {
    t23 = {
      lang,
      isFa: t22,
      isOwner,
      isListed,
      listingPrice,
      listingCurrencySymbol,
      isNftActive,
      currencySymbol,
      networkName,
      networkId,
      currencyId,
      listingQuantity,
      listingCurrencyAddress,
      flow,
      closeFlow,
      shared,
      refreshOffers,
      router,
      records,
      dismiss,
      sortedOffers,
      offersTotalCount,
      offersPageMeta,
      isOffersLoading,
      isOffersError,
      isOffersFetching,
      refetchOffers,
      setOffersPage,
      primary,
      openPrimary,
      openOffer,
      acceptOffer,
      cancelOffer,
      offerRights
    };
    $[48] = acceptOffer;
    $[49] = cancelOffer;
    $[50] = currencyId;
    $[51] = currencySymbol;
    $[52] = dismiss;
    $[53] = flow;
    $[54] = isListed;
    $[55] = isNftActive;
    $[56] = isOffersError;
    $[57] = isOffersFetching;
    $[58] = isOffersLoading;
    $[59] = isOwner;
    $[60] = lang;
    $[61] = listingCurrencyAddress;
    $[62] = listingCurrencySymbol;
    $[63] = listingPrice;
    $[64] = listingQuantity;
    $[65] = networkId;
    $[66] = networkName;
    $[67] = offerRights;
    $[68] = offersPageMeta;
    $[69] = offersTotalCount;
    $[70] = openOffer;
    $[71] = openPrimary;
    $[72] = primary;
    $[73] = records;
    $[74] = refetchOffers;
    $[75] = refreshOffers;
    $[76] = router;
    $[77] = shared;
    $[78] = sortedOffers;
    $[79] = t22;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  return t23;
}
function _temp4(address) {
  return typeof address === "string" && address.length > 0;
}
function _temp3(wallet) {
  return wallet?.address;
}
function _temp2() {}
function _temp(a, b) {
  return Number(b.total_price) - Number(a.total_price);
}
export type NftFinance = ReturnType<typeof useNftFinance>;